/**
 * lib/posthog-exception-filter.ts — drop unactionable browser noise before it
 * reaches PostHog Error Tracking. Four independent classes are suppressed:
 *
 * 1. Anonymized cross-origin "Script error." (GitHub #795 — PostHog watchdog P1
 *    "(unknown error)"): `posthog.startExceptionAutocapture()`
 *    (components/analytics/posthog-provider.tsx) hooks window.onerror /
 *    onunhandledrejection / console.error. When a THIRD-PARTY cross-origin
 *    script throws — Cloudflare Turnstile on the /site/[token] gate page, plus
 *    Clerk, Stripe, GTM, Microsoft Clarity, Meta Pixel — the browser redacts the
 *    event for security: window.onerror receives a bare "Script error." with no
 *    message, no stack, no source. PostHog has nothing to fingerprint, so every
 *    such hit collapses into a single "(unknown error)" Issue, which the
 *    marketing-runtime watchdog then files as a P1 with no stack to act on.
 *    Dropped ONLY when EVERY entry in `$exception_list` is both (a) message-empty
 *    or literally "Script error." AND (b) carries no stack frames. A genuine
 *    same-origin app exception always carries a stack from our own code, so this
 *    can never hide a real bug — if any entry has a stack or a real message, the
 *    whole event passes through untouched.
 *
 * 2. The benign "ResizeObserver loop …" browser notice: fired when a
 *    ResizeObserver callback mutates layout faster than the browser can deliver
 *    notifications in one frame. It is a well-known non-error the spec emits for
 *    diagnostics — no exception propagates, no user flow breaks — but browsers
 *    surface it via window.onerror, so autocapture files it as an Issue. Dropped
 *    by an exact whole-message match (the industry-standard
 *    `ignoreErrors` move Sentry ships for exactly these strings), independent
 *    of stack, because the message itself is the reliable fingerprint.
 *
 * 3. Stack-bearing Cloudflare Turnstile client errors (e.g.
 *    `TurnstileError: [Cloudflare Turnstile] Error: 300030`): Turnstile runs on
 *    the worker site-entrance gate (/site/[token]) and the lead forms
 *    (components/ui/turnstile-widget.tsx). Unlike case (1), these arrive WITH a
 *    real stack trace pointing into Cloudflare's own bundle
 *    (challenges.cloudflare.com/turnstile/v0/api.js), so the case-(1) stackless
 *    filter lets them through and each variant forms its own phantom Issue.
 *    These retryable challenge-execution failures (`110600`, `110620`,
 *    `200500`, `300*`, and `600*`) live entirely inside Cloudflare's script and
 *    cannot be fixed from our code. Suppression requires BOTH a documented
 *    retryable error code AND an unmistakable Turnstile provenance signal.
 *    Configuration failures such as invalid/disabled site keys or unauthorized
 *    domains stay visible.
 *
 * 4. Clerk network transport errors (e.g. `ClerkJS: Network error at
 *    ".../attempt_second_factor" - TypeError: Load failed`): the login page
 *    (app/(auth)/login/[[...login]]/page.tsx) renders Clerk's prebuilt
 *    `<SignIn>`, and clerk-js makes the two-factor call itself. `Load failed`
 *    (WebKit) and `Failed to fetch` (Chromium/Firefox) are the messages a
 *    browser gives a fetch that never completes. No first-party code sits in
 *    the path, so we cannot prevent it. Like case (3), the exception arrives
 *    WITH a stack pointing into Clerk's own bundle (clerk.browser.js), so the
 *    case-(2) stackless filter misses it. A single observation cannot tell a
 *    harmless client disconnect from a no-response Clerk outage. We therefore
 *    remove it from Error Tracking but rewrite it as a redacted, countable
 *    custom event; an HTTP-status failure or any mixed actionable entry remains
 *    a normal exception.
 *
 * Failure-direction invariant: uncertainty MUST fail toward reporting. Extra
 * vendor noise is tolerated; hiding an application/auth outage is not. An
 * event is suppressed or rewritten only when at least one target entry matches
 * and every other entry is either the same target class or already-proven
 * anonymous cross-origin noise. All four are handled in `before_send` — the
 * same mechanism that already drops transient ChunkLoadError deploy-skew
 * (lib/chunk-load-recovery.ts).
 */

interface ExceptionStackFrame {
  filename?: string;
  abs_path?: string;
  // posthog-js sends the raw source URL as `filename`; PostHog's ingestion
  // normalises it to `source`. before_send runs on the raw browser payload, but
  // matching `source` too future-proofs against SDK/normalisation changes.
  source?: string;
}

interface ExceptionListEntry {
  type?: string;
  value?: string;
  stacktrace?: { frames?: unknown[] } | null;
}

/** Minimal shape of a posthog-js CaptureResult we need for filtering. */
export interface ExceptionCaptureEvent {
  event?: string;
  properties?: {
    $exception_list?: ExceptionListEntry[];
  } & Record<string, unknown>;
}

/** The exact, redacted message browsers emit for a cross-origin script throw. */
const SCRIPT_ERROR_PATTERN = /^script error\.?$/i;

/**
 * The benign ResizeObserver loop notices browsers emit for diagnostics. Both
 * known variants are matched: the modern "loop completed with undelivered
 * notifications." and the older "loop limit exceeded". These never represent a
 * thrown exception or a broken flow — the observer simply rescheduled — so they
 * are pure noise in Error Tracking.
 *
 * Fully anchored to EXACTLY those two strings (like SCRIPT_ERROR_PATTERN, plus
 * an optional trailing dot): posthog-js strips any "Uncaught " prefix before
 * setting the entry value, so the notices land verbatim. A real app or library
 * error that merely mentions the observer (e.g. "ResizeObserver loop callback
 * failed", "Error in ResizeObserver loop handler: …") must NOT match, or an
 * actionable exception would be silently dropped.
 */
const RESIZE_OBSERVER_LOOP_PATTERN =
  /^resizeobserver loop (?:completed with undelivered notifications|limit exceeded)\.?$/i;

/**
 * Cloudflare's own Turnstile fingerprint. `TURNSTILE_MESSAGE_PATTERN` matches
 * the `[Cloudflare Turnstile]` tag Cloudflare prefixes onto every error it
 * throws (e.g. "[Cloudflare Turnstile] Error: 300030"), anywhere in the value.
 * `TURNSTILE_BUNDLE_PATTERN` matches a stack frame whose file is Cloudflare's
 * Turnstile bundle (challenges.cloudflare.com/turnstile/v0/api.js — the script
 * loaded by components/ui/turnstile-widget.tsx, optionally with a query string
 * and/or a further chunk path). Both are anchored to Cloudflare's own
 * type/message/host, so neither can match a same-origin app exception.
 */
const TURNSTILE_MESSAGE_PATTERN = /\[cloudflare turnstile\]/i;
const TURNSTILE_BUNDLE_PATTERN =
  /challenges\.cloudflare\.com\/turnstile\//i;
// Cloudflare documents the timeout codes 110600/110620, iframe-load failure
// 200500, and generic 300* / 600* challenge families as retryable. The last
// three digits of the generic families are internal and may vary. Do not
// suppress the other families: 110100/110110/110200 and 400020/400070, for
// example, identify actionable site-key, hostname, or disabled-key faults.
const RETRYABLE_TURNSTILE_CODE_PATTERN =
  /(?:^|\D)(?:110600|110620|200500|(?:300|600)\d{3})(?:\D|$)/;

function hasStackFrames(entry: ExceptionListEntry): boolean {
  const frames = entry.stacktrace?.frames;
  return Array.isArray(frames) && frames.length > 0;
}

/**
 * True when any stack frame of an entry has a file path matching `pattern`,
 * checking every path field posthog-js / PostHog may populate (`filename`,
 * `abs_path`, `source`). This is the shared provenance test behind the
 * Turnstile-bundle and Clerk-bundle checks.
 */
function hasStackFrameMatching(
  entry: ExceptionListEntry,
  pattern: RegExp,
): boolean {
  const frames = entry.stacktrace?.frames;
  if (!Array.isArray(frames)) return false;
  return frames.some((frame) => {
    const { filename, abs_path, source } = (frame ?? {}) as ExceptionStackFrame;
    return [filename, abs_path, source].some(
      (path) => typeof path === "string" && pattern.test(path),
    );
  });
}

/**
 * True when an exception-list entry is anonymized cross-origin noise: a
 * stackless entry whose message is empty or the browser's redacted
 * "Script error." placeholder. A stack — even on a "Script error." value —
 * means it is NOT redacted, so it is kept.
 */
function isAnonymousCrossOriginEntry(entry: ExceptionListEntry): boolean {
  if (hasStackFrames(entry)) return false;
  const value = typeof entry.value === "string" ? entry.value.trim() : "";
  return value === "" || SCRIPT_ERROR_PATTERN.test(value);
}

/**
 * Event-level safety boundary shared by the stack-bearing noise filters.
 * PostHog can place multiple causal exceptions in one `$exception_list`; one
 * recognised vendor entry must never erase an unrelated actionable sibling.
 * Anonymous stackless entries are safe companions because their own predicate
 * has already proved that they contain no reportable detail.
 */
function containsOnlyTargetOrAnonymousNoise(
  list: ExceptionListEntry[],
  isTarget: (entry: ExceptionListEntry) => boolean,
): boolean {
  let foundTarget = false;
  for (const entry of list) {
    if (isTarget(entry)) {
      foundTarget = true;
      continue;
    }
    if (!isAnonymousCrossOriginEntry(entry)) return false;
  }
  return foundTarget;
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is ENTIRELY
 * anonymized cross-origin noise (every entry message-empty/"Script error." AND
 * stackless) and should be dropped so it stops forming a phantom "(unknown
 * error)" Issue.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, and any exception where at least one entry carries a real
 * stack or a real message.
 */
export function isAnonymousCrossOriginException(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every(isAnonymousCrossOriginEntry);
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is a benign
 * "ResizeObserver loop …" browser notice and should be dropped so it stops
 * forming a phantom Issue. Matches on ANY entry's message (stack-independent) —
 * the message string is the reliable fingerprint, and the notice never carries
 * an actionable app stack.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, and any exception whose entries do not match.
 */
export function isBenignResizeObserverLoop(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return containsOnlyTargetOrAnonymousNoise(
    list,
    (entry) =>
      typeof entry.value === "string" &&
      RESIZE_OBSERVER_LOOP_PATTERN.test(entry.value),
  );
}

/**
 * True when an exception-list entry has unmistakable Cloudflare Turnstile
 * provenance: a `TurnstileError` type, a `[Cloudflare Turnstile]` message, or a
 * stack frame from Cloudflare's Turnstile bundle.
 */
function hasTurnstileProvenance(entry: ExceptionListEntry): boolean {
  if (typeof entry.type === "string" && entry.type.trim() === "TurnstileError") {
    return true;
  }
  if (
    typeof entry.value === "string" &&
    TURNSTILE_MESSAGE_PATTERN.test(entry.value)
  ) {
    return true;
  }
  return hasStackFrameMatching(entry, TURNSTILE_BUNDLE_PATTERN);
}

/**
 * Suppress only retryable generic challenge failures with Turnstile
 * provenance. Requiring the numeric family is deliberate: provenance alone
 * also identifies actionable Cloudflare configuration errors that must reach
 * monitoring.
 */
function isRetryableTurnstileEntry(entry: ExceptionListEntry): boolean {
  return (
    hasTurnstileProvenance(entry) &&
    typeof entry.value === "string" &&
    RETRYABLE_TURNSTILE_CODE_PATTERN.test(entry.value)
  );
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is a retryable
 * generic Cloudflare Turnstile challenge failure and should be dropped so it
 * stops forming a phantom Issue. Unlike `isAnonymousCrossOriginException`,
 * this catches the documented retryable Turnstile codes while preserving
 * actionable configuration families.
 *
 * Requires at least one matching entry and rejects the whole suppression when
 * any sibling is not the same retryable class or anonymous cross-origin noise.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, opaque Turnstile exceptions without a documented retryable
 * code, and configuration failures such as invalid keys or hostnames.
 */
export function isThirdPartyTurnstileException(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return containsOnlyTargetOrAnonymousNoise(list, isRetryableTurnstileEntry);
}

/**
 * Clerk's browser bundle. The version segment is pinned per release but bumps on
 * upgrade, so match the package and file, not the exact version — this covers
 * `.../npm/@clerk/clerk-js@5.127.1/dist/clerk.browser.js` whether served from
 * jsDelivr or the Clerk FAPI CNAME.
 */
const CLERK_BUNDLE_PATTERN =
  /@clerk\/clerk-js@[^\s"/]+\/dist\/clerk\.browser\.js/i;

/** Clerk's prefix for any SDK request that failed at the network boundary. */
const CLERK_NETWORK_ERROR_PATTERN = /clerkjs:\s*network error at\b/i;

/**
 * Clerk's own wording for a fetch that threw ("Network error at <url>"), paired
 * with the message a browser gives a request that never completed: "Load failed"
 * (WebKit) or "Failed to fetch" (Chromium/Firefox). Both mean a dropped
 * connection with no HTTP response — nothing first-party code can act on.
 */
const CLERK_NETWORK_DROP_PATTERN =
  /network error at\b.*\b(?:load failed|failed to fetch)\b/i;

/** Remove the request target while retaining the cause/status after it. */
const CLERK_NETWORK_REQUEST_PATTERN =
  /(clerkjs:\s*network error at)\s*(?:"[^"]*"|'[^']*'|\S+)/gi;

/** Defense in depth for Clerk attempt/session IDs repeated outside the URL. */
const CLERK_AUTH_IDENTIFIER_PATTERN =
  /\b(?:sia|sua|sess)_[a-z0-9_-]+\b/gi;

/**
 * A real HTTP failure carries a status code; a dropped connection never does.
 * If a "Network error" value ever also names a status, keep it — a short burst
 * of 4xx/5xx responses from Clerk is an actionable outage, not noise.
 */
const CLERK_HTTP_STATUS_PATTERN =
  /\b(?:status(?:\s*code)?[:=\s]+\d{3}\b|http\s+\d{3}\b)/i;

/** Countable replacement that does not enter PostHog Error Tracking. */
export const CLERK_NETWORK_ERROR_EVENT = "clerk_network_error_observed";

function isClerkNetworkExceptionEntry(entry: ExceptionListEntry): boolean {
  return (
    typeof entry.value === "string" &&
    CLERK_NETWORK_ERROR_PATTERN.test(entry.value) &&
    hasStackFrameMatching(entry, CLERK_BUNDLE_PATTERN)
  );
}

/**
 * Identify only a dropped-fetch from clerk-js. Requiring BOTH the
 * dropped-fetch signature AND a clerk.browser.js frame is deliberate: the
 * signature alone could match an app error that merely quotes the words, and a
 * Clerk value carrying an HTTP status is a real outage that must reach
 * monitoring.
 */
function isTransientClerkNetworkEntry(entry: ExceptionListEntry): boolean {
  if (typeof entry.value !== "string") return false;
  if (!isClerkNetworkExceptionEntry(entry)) return false;
  if (!CLERK_NETWORK_DROP_PATTERN.test(entry.value)) return false;
  if (CLERK_HTTP_STATUS_PATTERN.test(entry.value)) return false;
  return true;
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is a network
 * transport failure thrown by clerk-js (e.g. a 2FA `attempt_second_factor`
 * fetch that never completed) and is safe to reroute out of Error Tracking.
 *
 * Requires at least one matching entry and rejects the rewrite when any sibling
 * is not another matching Clerk failure or anonymous cross-origin noise.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, a Clerk value carrying an HTTP status (a real outage), and
 * any exception without Clerk-bundle provenance.
 */
export function isClerkNetworkDropException(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return containsOnlyTargetOrAnonymousNoise(list, isTransientClerkNetworkEntry);
}

function redactClerkAuthString(value: string): string {
  return value
    .replace(CLERK_NETWORK_REQUEST_PATTERN, '$1 "[redacted Clerk request]"')
    .replace(CLERK_AUTH_IDENTIFIER_PATTERN, "[redacted Clerk identifier]");
}

function redactClerkAuthValue(
  value: unknown,
  seen: WeakMap<object, unknown>,
): unknown {
  if (typeof value === "string") return redactClerkAuthString(value);
  if (value === null || typeof value !== "object") return value;

  const existing = seen.get(value);
  if (existing !== undefined) return existing;
  if (Array.isArray(value)) {
    const redacted: unknown[] = [];
    seen.set(value, redacted);
    value.forEach((item) => redacted.push(redactClerkAuthValue(item, seen)));
    return redacted;
  }
  if (
    Object.getPrototypeOf(value) !== Object.prototype &&
    Object.getPrototypeOf(value) !== null
  ) {
    return value;
  }

  const redacted: Record<string, unknown> = {};
  seen.set(value, redacted);
  for (const [key, nested] of Object.entries(value)) {
    redacted[key] = redactClerkAuthValue(nested, seen);
  }
  return redacted;
}

/**
 * Redact the request target and Clerk auth identifiers from every current or
 * future `$exception*` property on every exception event. Applying the narrow
 * string redactors unconditionally avoids making privacy depend on today’s
 * stack/provenance shape; unrelated exception text is left byte-for-byte intact.
 * Actionable status/cause failures remain `$exception` events with their cause,
 * status, frames, SDK ingestion fields and non-exception context intact.
 */
export function redactClerkNetworkExceptionDetails<
  T extends ExceptionCaptureEvent,
>(event: T): T {
  if (event.event !== "$exception") return event;

  const seen = new WeakMap<object, unknown>();
  const properties = Object.fromEntries(
    Object.entries(event.properties ?? {}).map(([key, value]) => [
      key,
      key.startsWith("$exception")
        ? redactClerkAuthValue(value, seen)
        : value,
    ]),
  );

  return { ...event, properties } as T;
}

/**
 * Rewrite a safely classified Clerk network exception into a custom analytics
 * signal. Every `$exception*` property is removed generically so the raw Clerk
 * request URL/sign-in-attempt identifier cannot leave this boundary and future
 * PostHog exception fields cannot silently reintroduce it. All ordinary SDK
 * ingestion fields and safe context remain intact, which keeps outage counts
 * observable without creating an Error Tracking issue for each browser blip.
 */
export function rewriteClerkNetworkDropAsSignal<
  T extends ExceptionCaptureEvent,
>(event: T): T {
  if (!isClerkNetworkDropException(event)) return event;

  const properties = Object.fromEntries(
    Object.entries(event.properties ?? {}).filter(
      ([key]) => !key.startsWith("$exception"),
    ),
  );

  return {
    ...event,
    event: CLERK_NETWORK_ERROR_EVENT,
    properties: {
      ...properties,
      clerk_error_kind: "network_transport",
    },
  } as T;
}
